import * as XLSX from 'xlsx';
import { getOrder } from '../services/api';

/**
 * Calculate total amount for a delivery
 * @param {object} order - Order object with items
 * @returns {number} Total amount
 */
const calculateDeliveryAmount = (order) => {
  if (!order || !order.items) return 0;

  const items = Array.isArray(order.items) ? order.items : [];
  const isCommon = order.isCommon !== false;

  // Calculate subtotal from items
  let subtotal = 0;
  items.forEach(item => {
    const qty = parseFloat(item.deliveredQuantity || item.pendingQuantity || item.quantity) || 0;
    const rate = parseFloat(item.unitPrice) || 0;
    subtotal += qty * rate;
  });

  // Calculate transport and loading charges
  const transportCharge = parseFloat(order.transportCharge) || 0;
  const loadingUnloadingCharge = parseFloat(order.loadingUnloadingCharge) || 0;

  // Calculate GST (only for common inquiries)
  let itemsGST = 0;
  let chargesGST = 0;

  if (isCommon) {
    // Calculate GST for each item
    itemsGST = items.reduce((sum, item) => {
      const qty = parseFloat(item.deliveredQuantity || item.pendingQuantity || item.quantity) || 0;
      const rate = parseFloat(item.unitPrice) || 0;
      const itemSubtotal = qty * rate;
      const gstPercentage = parseFloat(item.gstPercentage) || 0;
      return sum + (itemSubtotal * gstPercentage / 100);
    }, 0);

    // Calculate GST on charges
    const chargesAmount = transportCharge + loadingUnloadingCharge;
    const loadingGst = parseFloat(order.loadingGst) || 18;
    chargesGST = (chargesAmount * loadingGst) / 100;
  }

  const totalGST = itemsGST + chargesGST;
  const grandTotal = subtotal + transportCharge + loadingUnloadingCharge + totalGST;

  return grandTotal;
};

/**
 * Export deliveries to Excel
 * @param {Array} deliveries - Array of delivery objects
 * @param {Array} customers - Array of customer objects
 * @param {Array} sites - Array of site objects
 * @param {string} filename - Name of the file to download
 */
export const exportDeliveriesToExcel = async (deliveries, customers, sites, filename = 'deliveries.xlsx') => {
  // Helper function to get customer name
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  // Helper function to get site name
  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? (site.site_name || site.name) : '';
  };

  // Fetch order details for completed deliveries to get amounts
  const deliveriesWithAmounts = await Promise.all(
    deliveries.map(async (delivery) => {
      let amount = '-';

      // Only fetch amount for delivered status
      if (delivery.status === 'delivered') {
        try {
          const order = await getOrder(delivery.orderId);
          const totalAmount = calculateDeliveryAmount(order);
          amount = totalAmount > 0 ? `Rs. ${totalAmount.toFixed(2)}` : '-';
        } catch (error) {
          console.error(`Error fetching order for delivery ${delivery.id}:`, error);
        }
      }

      return { ...delivery, amount };
    })
  );

  // Prepare data for Excel
  const excelData = deliveriesWithAmounts.map((delivery, index) => ({
    'Sr. No.': index + 1,
    'Delivery Number': delivery.deliveryNumber || '',
    'Order Number': delivery.orderNumber || '-',
    'Delivery Date': delivery.deliveryDate ? new Date(delivery.deliveryDate).toLocaleDateString('en-IN') : '',
    'Customer Name': getCustomerName(delivery.customerId),
    'Site Name': getSiteName(delivery.siteId) || '-',
    'Vehicle Number': delivery.vehicleNumber || '-',
    'Driver Name': delivery.driverName || '-',
    'Driver Phone': delivery.driverPhone || '-',
    'Status': (delivery.status || 'in_transit').replace('_', ' ').toUpperCase(),
    'Delivery Amount': delivery.amount,
    'Remarks': delivery.remarks || ''
  }));

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Convert data to worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Set column widths
  const columnWidths = [
    { wch: 8 },  // Sr. No.
    { wch: 18 }, // Delivery Number
    { wch: 18 }, // Order Number
    { wch: 15 }, // Delivery Date
    { wch: 25 }, // Customer Name
    { wch: 25 }, // Site Name
    { wch: 18 }, // Vehicle Number
    { wch: 20 }, // Driver Name
    { wch: 15 }, // Driver Phone
    { wch: 12 }, // Status
    { wch: 18 }, // Delivery Amount
    { wch: 30 }  // Remarks
  ];
  ws['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Deliveries');

  // Generate Excel file and trigger download
  XLSX.writeFile(wb, filename);
};

/**
 * Export deliveries for a specific customer to Excel
 * @param {Array} deliveries - Array of delivery objects
 * @param {string} customerId - ID (UUID) of the customer to filter by
 * @param {Array} customers - Array of customer objects
 * @param {Array} sites - Array of site objects
 */
export const exportDeliveriesByCustomer = async (deliveries, customerId, customers, sites) => {
  const customer = customers.find(c => c.id === customerId);
  const customerName = customer ? customer.name : 'Unknown';

  // Filter deliveries by customer (compare as strings)
  const customerDeliveries = deliveries.filter(d => d.customerId === customerId);

  // Generate filename with customer name
  const filename = `Deliveries_${customerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

  await exportDeliveriesToExcel(customerDeliveries, customers, sites, filename);
};
